import { useAppDispatch } from "@/store";
import endpoints from "@/api/endpoints";
import httpClient from "@/api/http-client";
import { Product } from "@/models/category";
import { authActions, selectAuth } from "@/store/slices/auth";
import { fetchCartItems, selectCart } from "@/store/slices/cart";
import styles from "@/styles/product_list.module.scss";
import Tippy from "@tippyjs/react";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import fast from "../../assets/images/fast.png";
import similar from "../../assets/images/copy-icon.png";
import CurrencyInr from "../common/currency-inr";
import LoadingSpinner from "../common/loading-spinner";
import LoadingSpinnerSmall from "../common/loading-spinner-small";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as faHeartSolid } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { fetchWishlistItems, selectWishlist } from "@/store/slices/wishlist";
import { fbqTrackEvent } from "@/lib/fpixel";
import { initTryOnEventListener } from "@/lib/tryOnTracking";
import { syteTrackEvent } from "@/lib/syte";
import { S3ImageLoader } from "@/image-loaders/s3-image-loader";

type ProductCardPropsType = {
  product: Product;
};

const ProductCardWizzy = ({ product }: any) => {
  const dispatch = useAppDispatch();
  const [cartAddLoading, setCartAddLoading] = useState(false);
  const [cartRemoveLoading, setCartRemoveLoading] = useState(false);
  const [wishlistActionLoading, setwishlistActionLoading] = useState(false);
  const [actionData, setActionData] = useState({ type: "", id: "" });
  const cartState = useSelector(selectCart);
  const wishlistState = useSelector(selectWishlist);
  const authState = useSelector(selectAuth);
  const actionInProgress = useRef(false);
  const [tryOnAddToCart, setTryOnAddToCart] = useState(false);
  const [tryOnRemoveFromCart, setTryOnRemoveFromCart] = useState(false);

  const priceId = useMemo(
    () =>
      product.attributes?.find((v: any) => v.id === "price_id")?.values[0]
        ?.value[0],
    [product.attributes]
  );

  const cartItem = useMemo(
    () =>
      cartState.items.find((v) => v.identifier == (priceId || 0).toString()) ||
      null,
    [cartState.items, priceId]
  );

  const wishlistItem = useMemo(
    () => wishlistState.items.find((v) => v.id == product.id) || null,
    [wishlistState.items, product.id]
  );

  const addToCart = async () => {
    if (cartAddLoading) return;
    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      setActionData({ type: "cart", id: priceId });
      sessionStorage.setItem("add-to-cart-or-wishlist-id", priceId);
      return;
    }
    setCartAddLoading(true);
    try {
      await httpClient.post(endpoints.cart.add(), {
        is_solitaire: false,
        type: "product",
        identifier: priceId,
      });
      await dispatch(fetchCartItems());
      toast("Item added in cart", {
        type: "success",
        toastId: "cart-add-success",
      });
      fbqTrackEvent("AddToCart", {
        content_type: "product",
        content_ids: [product.sku[0]],
      });
      if (product.categories && product.categories.length) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: "add_to_cart",
          ecommerce: {
            currency: "AED",
            value: product.sellingPrice,
            items: [
              {
                item_id: product.sku[0],
                item_name: product.title,
                item_category: [...product.categories].reverse()[0].name,
                price: product.sellingPrice,
                quantity: 1,
              },
            ],
          },
        });
      }
      syteTrackEvent({
        name: "products_added_to_cart",
        tag: "ecommerce",
        osh_id: authState.user?.uuid,
        products: [
          {
            sku: product.sku[0],
            price: product.sellingPrice,
            quantity: 1,
          },
        ],
      });
    } catch (error) {}
    setCartAddLoading(false);
  };

  useEffect(() => {
    if (tryOnAddToCart) {
      setTryOnAddToCart(false);
      addToCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryOnAddToCart]);

  const removeFromCart = async (requireConfirmation = true) => {
    if (requireConfirmation) {
      if (!confirm("Are you sure you want to remove this product from cart?"))
        return;
    }
    setCartRemoveLoading(true);
    try {
      await httpClient.delete(endpoints.cart.remove(cartItem?.id));
      await dispatch(fetchCartItems());
      toast("Item removed from cart", {
        type: "info",
        toastId: "cart-remove-success",
      });
      if (product.categories && product.categories.length) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: "remove_from_cart",
          ecommerce: {
            currency: "AED",
            value: product.discount ? product.discount.price : product.price,
            items: [
              {
                item_id: product.sku[0],
                item_name: product.title,
                item_category: [...product.categories].reverse()[0].name,
                price: product.discount
                  ? product.discount.price
                  : product.price,
                quantity: 1,
              },
            ],
          },
        });
      }
    } catch (error) {
      console.log(error);
    }
    setCartRemoveLoading(false);
  };

  useEffect(() => {
    if (tryOnRemoveFromCart) {
      setTryOnRemoveFromCart(false);
      removeFromCart(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryOnRemoveFromCart]);

  const openTryOn = () => {
    if (window.initMirrarUI && process.env.NEXT_PUBLIC_STYLEDOTME_BRAND_ID) {
      initTryOnEventListener((name, data) => {
        if (name == "add-to-cart") {
          if (data.productCode == String(product.sku[0]).toUpperCase()) {
            setTryOnAddToCart(true);
          }
        }
        if (name == "ProductRemoveFromCart") {
          if (data.productCode == String(product.sku[0]).toUpperCase()) {
            setTryOnRemoveFromCart(true);
          }
        }
      });
      window.initMirrarUI(String(product.sku[0]).toUpperCase(), {
        brandId: process.env.NEXT_PUBLIC_STYLEDOTME_BRAND_ID,
      });
      if (product.categories && product.categories.length) {
        window.gtag("event", "tryon-button-click", {
          event_category: "mirrar-internal-event",
          event_label: `${[...product.categories].reverse()[0].name}/${
            product.sku[0]
          }/${product.discount ? product.discount.total : product.price}`,
        });
      }
    }
  };

  const addToWishlist = async () => {
    if (wishlistActionLoading) return;
    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      sessionStorage.setItem(
        "add-to-cart-or-wishlist-id",
        product.groupId ? product.groupId : product.id
      );
      setActionData({
        type: "wishlist",
        id: product.groupId ? product.groupId : product.id,
      });
      return;
    }
    setwishlistActionLoading(true);
    try {
      await httpClient.post(endpoints.wishlist.add(), {
        product_id: product.groupId ? product.groupId : product.id,
        user_id: authState.user.id,
      });
      await dispatch(fetchWishlistItems(authState.user.id as any));
      toast("Item added in wishlist", {
        type: "success",
        toastId: "withlist-add-success",
      });
      fbqTrackEvent("AddToWishlist", {
        content_type: "product",
        content_ids: [product.sku[0]],
      });
    } catch (error) {}
    setwishlistActionLoading(false);
  };

  const removeFromWishlist = async () => {
    if (wishlistActionLoading) return;
    if (!confirm("Are you sure you want to remove this product from wishlist?"))
      return;
    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      return;
    }
    setwishlistActionLoading(true);
    try {
      await httpClient.post(endpoints.wishlist.remove(), {
        product_id: product.id,
        user_id: authState.user.id,
      });
      await dispatch(fetchWishlistItems(authState.user.id as any));
      toast("Item removed from wishlist", {
        type: "success",
        toastId: "withlist-remove-success",
      });
    } catch (error) {}
    setwishlistActionLoading(false);
  };

  useEffect(() => {
    const handleAction = async () => {
      if (actionInProgress.current) return;

      if (actionData.id && authState.user) {
        actionInProgress.current = true;

        try {
          if (actionData.type === "cart") {
            await addToCart();
          } else if (actionData.type === "wishlist") {
            await addToWishlist();
          }
        } catch (error) {
          console.error("Error handling action:", error);
        } finally {
          setActionData({ type: "", id: "" });
          sessionStorage.removeItem("add-to-cart-or-wishlist-id");
          actionInProgress.current = false;
        }
      }
    };
    const cart_id = sessionStorage.getItem("add-to-cart-or-wishlist-id");
    if (`${actionData?.id}` === cart_id && authState.user) {
      handleAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, actionData]);

  return (
    <div className={styles.product}>
      <div className={styles.pad_10}>
        <div className={styles.products_text_top}>
          <div className={styles.product_top_left}>
            {!!product.attributes?.find((v: any) => v.id === "tags")?.values[0]
              ?.value[0] && (
              <div className={styles.new_arrival}>
                {
                  product.attributes?.find((v: any) => v.id === "tags")
                    ?.values[0]?.value[0]
                }
              </div>
            )}
            {/* {!!product.best_seller && (
              <div className={styles.best_seller}>BESTSELLER</div>
            )} */}
            {!!product.attributes?.find(
              (v: any) => v.id === "deal_of_the_day"
            ) && <div className={styles.trending}>Deal of The Day</div>}
            {!!product.attributes?.find((v: any) => v.id === "discount_name")
              ?.values[0]?.value[0] && (
              <>
                <div className={styles.discount}>
                  {
                    product.attributes?.find(
                      (v: any) => v.id === "discount_name"
                    )?.values[0]?.value[0]
                  }
                </div>
                {/* <div className={styles.text}>
                  ON {product.discount?.name?.split("On")[1].toUpperCase()}
                </div> */}
              </>
            )}
          </div>
          <div className={styles.cart_top}>
            {/* {!!product.attributes?.find((v: any) => v.id === "try_online") && (
              <div className={styles.tryontext} onClick={openTryOn}>
                <span>TryOn</span>
              </div>
            )} */}
            {/* {!!product.attributes?.find(
              (v: any) => v.id === "fast_delivery"
            ) && (
              <Tippy content="Fast Delivery">
                <div className={styles.fast_delivery}>
                  <Image src={fast} alt="Fast Delivery" priority unoptimized />
                </div>
              </Tippy>
            )} */}
            {wishlistActionLoading && (
              <span style={{ position: "relative", top: -6 }}>
                <LoadingSpinnerSmall />
              </span>
            )}
            {!wishlistActionLoading && (
              <>
                {wishlistItem ? (
                  <Tippy content="Wishlisted">
                    <a className="pointer" onClick={removeFromWishlist}>
                      <FontAwesomeIcon
                        icon={faHeartSolid}
                        width={20}
                        color={"#FF1933"}
                        style={{ position: "relative", top: -8 }}
                      />
                    </a>
                  </Tippy>
                ) : (
                  <Tippy content="Add To Wishlist">
                    <a className="pointer" onClick={addToWishlist}>
                      <FontAwesomeIcon
                        icon={faHeartRegular}
                        width={20}
                        color={"#FF1933"}
                        style={{ position: "relative", top: -8 }}
                      />
                    </a>
                  </Tippy>
                )}
              </>
            )}
          </div>
        </div>
        <Link href={product.url} passHref>
          <a target="_blank" rel="noopener noreferrer">
            <div className={styles.product_image}>
              <Image
                src={product.mainImage}
                className={styles.img_fluid}
                alt={product.name}
                width={2500}
                height={2500}
                sizes="(max-width: 479px) 148px, (max-width: 640px) 200px, (max-width: 991px) 318px, (max-width: 1199px) 200px, (max-width: 1600px) 245px, (max-width: 1920px) 50vw, 100vw"
                loader={S3ImageLoader}
              />
            </div>
          </a>
        </Link>
        {/* <button
          aria-label="PLP-button"
          className={`${styles.similar_button} syte-discovery`}
          data-image-src={product.mainImage}
          data-sku={product.sku[0]}
          data-placement="PLP-button"
        >
          <Image src={similar} alt="View Similar" />{" "}
          <span className={styles.text}>View Similar</span>
        </button> */}
        {/* <button
          aria-label="PLP-button"
          className={`${styles.similar_button_mobile} syte-discovery`}
          data-image-src={product.mainImage}
          data-sku={product.sku[0]}
          data-placement="PLP-button"
        >
          <Image src={similar} alt="View Similar" />
        </button> */}
        <p className={styles.product_text_d}>{product.name}</p>
        <div className={styles.price_cart}>
          <div className={styles.price1}>
            <span>
              <CurrencyInr value={product.sellingPrice} />
            </span>
            <br />
            {product.sellingPrice !== product.price && (
              <div className={styles.price2}>
                <span>
                  <CurrencyInr value={product.price} />
                </span>
              </div>
            )}
          </div>

          {!cartItem && (
            <>
              {cartAddLoading && <LoadingSpinner />}
              {!cartAddLoading && (
                <Tippy content="Add To Cart">
                  <a
                    className={`${styles.details} pointer`}
                    onClick={addToCart}
                  >
                    <i className="bi bi-cart-plus"></i>
                  </a>
                </Tippy>
              )}
            </>
          )}

          {cartItem && (
            <>
              {cartRemoveLoading && <LoadingSpinner />}
              {!cartRemoveLoading && (
                <Tippy content="Remove From Cart">
                  <a
                    className={`${styles.details} pointer`}
                    onClick={() => removeFromCart()}
                  >
                    <i className="bi bi-cart-dash-fill"></i>
                  </a>
                </Tippy>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCardWizzy;
